import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import styled from 'styled-components';

import { 
  Button, 
  DropDown, 
  Label,
  PageContainer,
  PageTitle,
  PageSubtitle,
  Section,
  LoadingMessage,
  ErrorMessage,
  NoDataMessage
} from '@dized/ui';
import AnalyticsActions from '../../actions/AnalyticsActions';
import GameActions from '../../actions/GameActions';
import ProjectActions from '../../actions/ProjectActions';
import GameDetails from '../../components/GameDetails';
import { DateRangePicker } from '../../components/DateRangePicker';
import { ProjectDescription } from '../../components/ProjectDescription';
import { QuickStats } from '../../components/QuickStats';
import { UserDistribution } from '../../components/UserDistribution';
import { LanguageDistribution } from '../../components/LanguageDistribution';
import { UserMilestones } from '../../components/UserMilestones';
import { StatsOverview } from '../../components/StatsOverview';
import { StatePerformanceAnalytics } from '../../components/StatePerformanceAnalytics';
import DailyStats from '../../components/DailyStats';
// Hooks are used within class components through state management
import { getStringFromTO } from '../../common/utils/i18nUtils';
import './Analytics.css';

const ControlsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr auto;
  gap: 20px;
  align-items: end;
  margin-bottom: 20px;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const mapStateToProps = (store) => ({
  analytics: store.analytics,
  organization: store.db.organization,
  games: store.db.games,
  projects: store.db.projects
});

const mapDispatchToProps = (dispatch) => ({
  ...AnalyticsActions.bindActions(dispatch),
  ...GameActions.bindActions(dispatch),
  ...ProjectActions.bindActions(dispatch)
});

class Analytics extends Component {
  static propTypes = {
    match: PropTypes.shape({
      params: PropTypes.shape({
        orgName: PropTypes.string,
        gameId: PropTypes.string,
        projectId: PropTypes.string
      })
    }).isRequired,
    history: PropTypes.object.isRequired,
    location: PropTypes.object.isRequired,
    analytics: PropTypes.object.isRequired,
    organization: PropTypes.object.isRequired,
    games: PropTypes.object.isRequired,
    projects: PropTypes.object.isRequired,
    GET_PROJECT_STATS: PropTypes.func.isRequired,
    GET_PROJECT_DAILY_STATS: PropTypes.func.isRequired,
    GET_GAMES: PropTypes.func.isRequired,
    GET_PROJECTS: PropTypes.func.isRequired,
    GET_LATEST_REVISION: PropTypes.func.isRequired
  };

  state = {
    dateRange: {
      from: this.getDateNDaysAgo(28),
      to: this.getYesterday()
    },
    datePreset: '28days', // Track which preset is selected
    selectedGameId: '',
    selectedProjectId: ''
  };

  // Utility method to parse query parameters
  parseQueryParams = () => {
    const { location } = this.props;
    const searchParams = new URLSearchParams(location.search);
    const datePreset = searchParams.get('datePreset') || '28days';
    
    // For custom range, use the specific dates from URL
    if (datePreset === 'custom') {
      return {
        gameId: searchParams.get('gameId') || '',
        projectId: searchParams.get('projectId') || '',
        datePreset: datePreset,
        dateFrom: searchParams.get('dateFrom') || this.getDateNDaysAgo(28),
        dateTo: searchParams.get('dateTo') || this.getYesterday()
      };
    }
    
    // For preset selections, calculate dates dynamically
    return {
      gameId: searchParams.get('gameId') || '',
      projectId: searchParams.get('projectId') || '',
      datePreset: datePreset,
      dateFrom: this.getDateFromPreset(datePreset).from,
      dateTo: this.getDateFromPreset(datePreset).to
    };
  };

  // Utility method to update URL with current parameters
  updateURL = () => {
    const { history, location } = this.props;
    const { selectedGameId, selectedProjectId, dateRange, datePreset } = this.state;
    
    const searchParams = new URLSearchParams(location.search);
    
    // Clear all parameters first to ensure clean state
    searchParams.delete('gameId');
    searchParams.delete('projectId');
    searchParams.delete('datePreset');
    searchParams.delete('dateFrom');
    searchParams.delete('dateTo');
    
    // Add current parameters
    if (selectedGameId) {
      searchParams.set('gameId', selectedGameId);
    }
    if (selectedProjectId) {
      searchParams.set('projectId', selectedProjectId);
    }
    if (datePreset) {
      searchParams.set('datePreset', datePreset);
    }
    
    // Only include date parameters for custom range selection
    if (datePreset === 'custom' && dateRange.from && dateRange.to) {
      searchParams.set('dateFrom', dateRange.from);
      searchParams.set('dateTo', dateRange.to);
    }
    
    const queryString = searchParams.toString();
    const newURL = `${location.pathname}${queryString ? `?${queryString}` : ''}`;
    
    // Only update URL if it's different from current
    if (location.pathname + location.search !== newURL) {
      history.replace(newURL);
    }
  };

  getCurrentDate() {
    const today = new Date();
    return today.toISOString().split('T')[0];
  }

  getYesterday() {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  }

  getDateNDaysAgo(days) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
  }

  // Helper method to get date range from preset
  getDateFromPreset = (preset) => {
    const ANALYTICS_START_DATE = '2025-07-01';
    const yesterday = this.getYesterday();
    
    switch (preset) {
      case '7days':
        return { from: this.getDateNDaysAgo(7), to: yesterday };
      case '28days':
        return { from: this.getDateNDaysAgo(28), to: yesterday };
      case '90days':
        return { from: this.getDateNDaysAgo(90), to: yesterday };
      case 'alltime':
        return { from: ANALYTICS_START_DATE, to: yesterday };
      case 'custom': {
        // For custom, return current date range if available, otherwise default to 28 days
        const currentFrom = this.state?.dateRange?.from;
        const currentTo = this.state?.dateRange?.to;
        return { 
          from: currentFrom || this.getDateNDaysAgo(28), 
          to: currentTo || yesterday 
        };
      }
      default:
        return { from: this.getDateNDaysAgo(28), to: yesterday };
    }
  };

  componentDidMount() {
    const { match, organization, games, GET_GAMES } = this.props;
    const { gameId, projectId } = match.params;

    // Parse query parameters for initial state
    const queryParams = this.parseQueryParams();

    // Determine final values with priority: query params > URL params > defaults
    const finalGameId = queryParams.gameId || gameId || '';
    const finalProjectId = queryParams.projectId || projectId || '';
    const finalDatePreset = queryParams.datePreset;
    const finalDateFrom = queryParams.dateFrom;
    const finalDateTo = queryParams.dateTo;

    // Set complete state in one go
    this.setState({
      dateRange: {
        from: finalDateFrom,
        to: finalDateTo
      },
      datePreset: finalDatePreset,
      selectedGameId: finalGameId,
      selectedProjectId: finalProjectId
    }, () => {
      // Load games list for dropdown only if not already initialized
      if (organization.uuid && !games.initialized) {
        GET_GAMES(organization.uuid);
      }

      // Load complete game data with assets if we have a game selected
      if (finalGameId) {
        this.ensureGameDataLoaded(finalGameId);
      }

      // Load analytics if we have both game and project
      if (finalGameId && finalProjectId) {
        this.loadAnalytics();
      }
    });

    document.title = 'Dized Portal: Analytics';
  }

  // Helper method to ensure complete game data is loaded
  ensureGameDataLoaded = (gameId) => {
    const { GET_LATEST_REVISION } = this.props;
    if (gameId) {
      GET_LATEST_REVISION(gameId);
      this.loadProjectsForGame(gameId);
    }
  };

  componentDidUpdate(prevProps) {
    const { organization: prevOrganization, location: prevLocation, games: prevGames } = prevProps;
    const { organization: currentOrganization, games: currentGames, GET_GAMES, location: currentLocation } = this.props;
    const { selectedGameId } = this.state;

    // Handle URL changes (back/forward navigation) - but not on initial load
    if (prevLocation.search !== currentLocation.search && prevLocation.search !== '') {
      const queryParams = this.parseQueryParams();
      this.setState({
        dateRange: {
          from: queryParams.dateFrom,
          to: queryParams.dateTo
        },
        datePreset: queryParams.datePreset, // Update datePreset from URL
        selectedGameId: queryParams.gameId,
        selectedProjectId: queryParams.projectId
      }, () => {
        // Reload data if project is selected
        if (queryParams.projectId) {
          this.loadAnalytics();
        } else if (queryParams.gameId) {
          this.ensureGameDataLoaded(queryParams.gameId);
        }
      });
    }

    // Handle organization change - load games if not initialized
    if (currentOrganization.uuid !== prevOrganization.uuid && currentOrganization.uuid && !currentGames.initialized) {
      GET_GAMES(currentOrganization.uuid);
    }

    // Handle case where games are loaded for the first time and we have a selected game
    if (!prevGames.initialized && currentGames.initialized && selectedGameId) {
      this.ensureGameDataLoaded(selectedGameId);
    }

    // Handle case where game data becomes available but needs complete asset data
    if (selectedGameId && currentGames.gameLookup && prevGames.gameLookup !== currentGames.gameLookup) {
      const currentGame = currentGames.gameLookup[selectedGameId];
      const prevGame = prevGames.gameLookup ? prevGames.gameLookup[selectedGameId] : null;
      
      if (currentGame && (!currentGame.assets || !prevGame)) {
        this.ensureGameDataLoaded(selectedGameId);
      }
    }
  }
  
  loadProjectsForGame = (gameId) => {
    const { GET_PROJECTS } = this.props;
    if (gameId) {
      GET_PROJECTS(gameId, gameId);
    }
  }

  loadAnalytics = () => {
    const { selectedProjectId } = this.state;
    const { from, to } = this.state.dateRange;

    // Only load analytics if a specific project is selected
    if (selectedProjectId) {
      const actionResult = this.props.GET_PROJECT_STATS(selectedProjectId, from, to);
      const dailyStatsResult = this.props.GET_PROJECT_DAILY_STATS(selectedProjectId, from, to);
      
      // If the action returns a promise, log its resolution
      if (actionResult && typeof actionResult.then === 'function') {
        actionResult
          .then((result) => {
            // Action completed successfully
          })
          .catch((error) => {
            console.error('🔍 Action promise rejected with:', error);
          });
      }

      // Handle daily stats promise
      if (dailyStatsResult && typeof dailyStatsResult.then === 'function') {
        dailyStatsResult
          .then((result) => {
            console.log('📅 Daily stats loaded successfully');
          })
          .catch((error) => {
            console.error('📅 Daily stats action promise rejected with:', error);
          });
      }
    }
  };

  handleGameChange = (event) => {
    const gameId = event.target.value;
    
    this.setState({ 
      selectedGameId: gameId,
      selectedProjectId: '' // Reset project selection
    }, () => {
      this.updateURL();
    });
    
    if (gameId) {
      this.ensureGameDataLoaded(gameId);
    }
  };

  handleProjectChange = (event) => {
    const projectId = event.target.value;
    this.setState({ selectedProjectId: projectId }, () => {
      this.updateURL();
      // Auto-fetch data when project is selected
      if (projectId) {
        this.loadAnalytics();
      }
    });
  };

  handleFetchData = () => {
    this.loadAnalytics();
  };

  handleDateChange = (from, to) => {
    // Check if this date range matches any preset
    const matchingPreset = this.findMatchingPreset(from, to);
    
    this.setState({
      dateRange: { from, to },
      // Only set to custom if the date range doesn't match any preset
      datePreset: matchingPreset || 'custom'
    }, () => {
      this.updateURL();
      this.loadAnalytics();
    });
  };

  // Helper method to check if date range matches a preset
  findMatchingPreset = (from, to) => {
    const presets = ['7days', '28days', '90days', 'alltime'];
    
    for (const preset of presets) {
      const presetRange = this.getDateFromPreset(preset);
      if (presetRange.from === from && presetRange.to === to) {
        return preset;
      }
    }
    
    return null; // No matching preset found
  };

  handlePresetChange = (preset) => {
    // If not custom, update the date range based on preset
    if (preset !== 'custom') {
      const newDateRange = this.getDateFromPreset(preset);
      this.setState({ 
        datePreset: preset,
        dateRange: newDateRange
      }, () => {
        this.updateURL();
        this.loadAnalytics();
      });
    } else {
      // For custom, just update the preset and ensure we have valid date range
      const currentRange = this.state.dateRange;
      const validRange = {
        from: currentRange.from || this.getDateNDaysAgo(28),
        to: currentRange.to || this.getYesterday()
      };
      
      this.setState({ 
        datePreset: preset,
        dateRange: validRange
      }, () => {
        this.updateURL();
      });
    }
  };
  
  handleRefreshData = () => {
    this.loadAnalytics();
  };

  getAnalyticsData = () => {
    const { analytics } = this.props;
    const { selectedProjectId } = this.state;

    if (selectedProjectId) {
      const projectData = analytics.projectStats[selectedProjectId];
      
      // Return the full array for components that need all language data
      const result = Array.isArray(projectData) && projectData.length > 0 ? projectData : null;
      return result;
    }
    
    return null;
  };

  getDailyStatsData = () => {
    const { analytics } = this.props;
    const { selectedProjectId } = this.state;

    if (selectedProjectId) {
      const dailyData = analytics.projectDailyStats[selectedProjectId];
      
      // Return the daily stats array
      const result = Array.isArray(dailyData) && dailyData.length > 0 ? dailyData : null;
      return result;
    }
    
    return null;
  };

  // Helper method to get single item for components that need just one language
  getAnalyticsDataSingle = () => {
    const fullData = this.getAnalyticsData();
    return Array.isArray(fullData) && fullData.length > 0 ? fullData[0] : null;
  };

  // Helper method to get aggregated data across all languages
  getAnalyticsDataAggregated = () => {
    const fullData = this.getAnalyticsData();
    if (!Array.isArray(fullData) || fullData.length === 0) return null;

    // If only one language, return it directly
    if (fullData.length === 1) return fullData[0];

    // Aggregate data across all languages
    const aggregated = {
      locale: 'All Languages',
      project_type: fullData[0].project_type, // Same across all languages
      session_count: 0,
      total_player_count: 0,
      total_player_count_based_on: 0, // Add missing field for "Set Player" events
      total_time_played: 0,
      avg_time_played: 0,
      median_time_played: 0,
      total_blocks_advanced: 0,
      // State completion percentages - weighted by player count
      state_intro_completed_percentage: 0,
      state_setup_completed_percentage: 0,
      state_teaching_completed_percentage: 0,
      state_firstturn_completed_percentage: 0,
      state_gameplay_completed_percentage: 0,
      state_tutorial_completed_percentage: 0,
      // State median time and blocks - averaged across locales
      state_intro_median_time_played: 0,
      state_setup_median_time_played: 0,
      state_teaching_median_time_played: 0,
      state_firstturn_median_time_played: 0,
      state_gameplay_median_time_played: 0,
      state_tutorial_median_time_played: 0,
      state_intro_median_blocks_advanced: 0,
      state_setup_median_blocks_advanced: 0,
      state_teaching_median_blocks_advanced: 0,
      state_firstturn_median_blocks_advanced: 0,
      state_gameplay_median_blocks_advanced: 0,
      state_tutorial_median_blocks_advanced: 0,
      // Geographic data - aggregate country distribution across all languages
      country_distribution: []
    };

    const countryMap = new Map();

    // Sum up totals and aggregate geographic data
    fullData.forEach(langData => {
      aggregated.session_count += langData.session_count || 0;
      aggregated.total_player_count += langData.total_player_count || 0;
      aggregated.total_player_count_based_on += langData.total_player_count_based_on || 0;
      aggregated.total_time_played += langData.total_time_played || 0;
      aggregated.total_blocks_advanced += langData.total_blocks_advanced || 0;
      
      // Aggregate country distribution data across all languages
      if (langData.country_distribution && Array.isArray(langData.country_distribution)) {
        langData.country_distribution.forEach(countryData => {
          const countryName = countryData.country;
          if (countryMap.has(countryName)) {
            countryMap.set(countryName, countryMap.get(countryName) + countryData.count);
          } else {
            countryMap.set(countryName, countryData.count);
          }
        });
      }
    });

    // Convert country map to array and calculate percentages
    const totalSessions = aggregated.session_count;
    if (countryMap.size > 0 && totalSessions > 0) {
      aggregated.country_distribution = Array.from(countryMap.entries())
        .map(([country, count]) => ({
          country,
          count,
          percentage: parseFloat(((count / totalSessions) * 100).toFixed(1))
        }))
        .sort((a, b) => b.count - a.count); // Sort by count descending
    }

    // Calculate weighted averages for completion percentages
    fullData.forEach(langData => {
      const playerWeight = (langData.total_player_count || 0) / aggregated.total_player_count;
      
      aggregated.state_intro_completed_percentage += (langData.state_intro_completed_percentage || 0) * playerWeight;
      aggregated.state_setup_completed_percentage += (langData.state_setup_completed_percentage || 0) * playerWeight;
      aggregated.state_teaching_completed_percentage += (langData.state_teaching_completed_percentage || 0) * playerWeight;
      aggregated.state_firstturn_completed_percentage += (langData.state_firstturn_completed_percentage || 0) * playerWeight;
      aggregated.state_gameplay_completed_percentage += (langData.state_gameplay_completed_percentage || 0) * playerWeight;
      aggregated.state_tutorial_completed_percentage += (langData.state_tutorial_completed_percentage || 0) * playerWeight;
    });

    // Calculate average time played by averaging all locales' avg_time_played, skipping zero/invalid values
    const validAvgTimePlayed = fullData
      .map(langData => langData.avg_time_played)
      .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
    
    aggregated.avg_time_played = validAvgTimePlayed.length > 0
      ? validAvgTimePlayed.reduce((sum, value) => sum + value, 0) / validAvgTimePlayed.length
      : 0;

    // Calculate median time played by averaging all locales' median_time_played, skipping zero/invalid values
    const validMedianTimePlayed = fullData
      .map(langData => langData.median_time_played)
      .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
    
    aggregated.median_time_played = validMedianTimePlayed.length > 0
      ? validMedianTimePlayed.reduce((sum, value) => sum + value, 0) / validMedianTimePlayed.length
      : 0;

    // Calculate avg_players_per_session by averaging all locales together, skipping zero/non-integer values
    const validAvgPlayersPerSession = fullData
      .map(langData => langData.avg_players_per_session)
      .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
    
    aggregated.avg_players_per_session = validAvgPlayersPerSession.length > 0
      ? validAvgPlayersPerSession.reduce((sum, value) => sum + value, 0) / validAvgPlayersPerSession.length
      : 0;

    // Calculate avg_blocks_advanced_per_session by averaging all locales, skipping zero/invalid values
    const validAvgBlocksPerSession = fullData
      .map(langData => langData.avg_blocks_advanced_per_session)
      .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
    
    aggregated.avg_blocks_advanced_per_session = validAvgBlocksPerSession.length > 0
      ? validAvgBlocksPerSession.reduce((sum, value) => sum + value, 0) / validAvgBlocksPerSession.length
      : 0;

    // Calculate avg_time_per_session by averaging (total_time_played / session_count) across all locales
    const validAvgTimePerSession = fullData
      .map(langData => {
        const sessions = langData.session_count || 0;
        const totalTime = langData.total_time_played || 0;
        return sessions > 0 ? totalTime / sessions : 0;
      })
      .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
    
    aggregated.avg_time_per_session = validAvgTimePerSession.length > 0
      ? validAvgTimePerSession.reduce((sum, value) => sum + value, 0) / validAvgTimePerSession.length
      : 0;

    // Aggregate state median time fields by averaging all locales, skipping null/zero/invalid values
    const stateTimeFields = [
      'state_intro_median_time_played',
      'state_setup_median_time_played', 
      'state_teaching_median_time_played',
      'state_firstturn_median_time_played',
      'state_gameplay_median_time_played',
      'state_tutorial_median_time_played'
    ];

    stateTimeFields.forEach(field => {
      const validValues = fullData
        .map(langData => langData[field])
        .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
      
      aggregated[field] = validValues.length > 0
        ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
        : 0;
    });

    // Aggregate state median blocks fields by averaging all locales, skipping null/zero/invalid values
    const stateBlocksFields = [
      'state_intro_median_blocks_advanced',
      'state_setup_median_blocks_advanced',
      'state_teaching_median_blocks_advanced', 
      'state_firstturn_median_blocks_advanced',
      'state_gameplay_median_blocks_advanced',
      'state_tutorial_median_blocks_advanced'
    ];

    stateBlocksFields.forEach(field => {
      const validValues = fullData
        .map(langData => langData[field])
        .filter(value => typeof value === 'number' && !isNaN(value) && value > 0);
      
      aggregated[field] = validValues.length > 0
        ? validValues.reduce((sum, value) => sum + value, 0) / validValues.length
        : 0;
    });

    return aggregated;
  };

  getPageTitle = () => {
    return 'Project Analytics';
  };

  getPageSubtitle = () => {
    const { selectedProjectId, selectedGameId } = this.state;
    
    if (selectedProjectId) {
      return 'Analytics data for the selected project';
    }
    if (selectedGameId) {
      return 'Please select a project to view analytics data';
    }
    return 'Please select a game and project to view analytics data';
  };

  getGameOptions = () => {
    const { games } = this.props;
    const gameList = games.gameList || [];
    const gameLookup = games.gameLookup || {};

    const options = [{ label: 'Select a game...', value: '' }];
    
    gameList.forEach(gameId => {
      const game = gameLookup[gameId];
      if (game) {
        let gameTitle;
        
        // Test with hardcoded string first
        if (game.title && game.title.text) {
          gameTitle = game.title.text;
        } else {
          try {
            gameTitle = getStringFromTO(game.title);
          } catch (error) {
            console.error('🔍 ERROR in getStringFromTO:', error);
            gameTitle = game.name || 'Unknown Game';
          }
        }
        
        // Ensure we have a string
        if (typeof gameTitle !== 'string') {
          console.warn('🔍 WARNING - gameTitle is not a string, converting:', gameTitle);
          gameTitle = String(gameTitle);
        }
        
        options.push({
          label: gameTitle,
          value: game.uuid
        });
      }
    });

    return options;
  };

  getProjectOptions = () => {
    const { projects } = this.props;
    const { selectedGameId } = this.state;
    const projectList = projects.projects || [];

    const options = [{ label: 'Select a project...', value: '' }];
    
    if (selectedGameId) {
      // Filter projects by the selected game ID and include 'tct', 'rules', and 'extra' types
      const filteredProjects = projectList.filter(project => {
        return project.gameId === selectedGameId && (project.type === 'tct' || project.type === 'rules' || project.type === 'extra');
      });
      
      // Add real projects to options with type prefixes
      filteredProjects.forEach(project => {
        const projectTitle = getStringFromTO(project.title);
        let displayLabel;
        
        if (project.type === 'tct') {
          displayLabel = `Tutorial: ${projectTitle}`;
        } else if (project.type === 'rules') {
          displayLabel = `Rules: ${projectTitle}`;
        } else if (project.type === 'extra') {
          displayLabel = `Extra: ${projectTitle}`;
        } else {
          // Fallback for any other types
          displayLabel = projectTitle;
        }
        
        options.push({
          label: displayLabel + ` (${project.uuid.substring(0, 8)})`,
          value: project.uuid
        });
      });
      
      // If no real projects found, the dropdown will just show "Select a project..."
      if (filteredProjects.length === 0) {
        // No projects found for this game
      }
    }

    return options;
  };

  renderControls = () => {
    const { selectedGameId, selectedProjectId } = this.state;
    const { analytics } = this.props;
    const isLoading = analytics.loading;
    
    const gameOptions = this.getGameOptions();

    return (
      <Section>
        <ControlsGrid>
          <ControlGroup>
            <Label>Game</Label>
            <DropDown
              value={selectedGameId}
              onChange={this.handleGameChange}
              options={gameOptions}
            />
          </ControlGroup>
          
          <ControlGroup>
            <Label>Project</Label>
            <DropDown
              value={selectedProjectId}
              onChange={this.handleProjectChange}
              options={this.getProjectOptions()}
              disabled={!selectedGameId}
            />
          </ControlGroup>

          <Button 
            onClick={this.handleFetchData}
            disabled={!selectedProjectId || isLoading}
          >
            {isLoading ? 'Loading...' : 'Refetch Data'}
          </Button>
        </ControlsGrid>
        
        <DateRangePicker
          onDateChange={this.handleDateChange}
          onPresetChange={this.handlePresetChange}
          initialPreset={this.state.datePreset}
          initialDateRange={this.state.dateRange}
          loading={isLoading}
        />
      </Section>
    );
  };

  getSelectedProject = () => {
    const { projects } = this.props;
    const { selectedProjectId } = this.state;
    
    if (selectedProjectId && projects.projects) {
      return projects.projects.find(p => p.uuid === selectedProjectId);
    }
    return null;
  };

  getSelectedGame = () => {
    const { games } = this.props;
    const { selectedGameId } = this.state;
    
    if (selectedGameId && games.gameLookup) {
      return games.gameLookup[selectedGameId];
    }
    return null;
  };

  // Helper function to normalize game data for ProjectDescription component
  getNormalizedGame = () => {
    const game = this.getSelectedGame();
    if (!game) return null;

    const extractedTitle = getStringFromTO(game.title) || game.name || 'Unknown Game';

    // Ensure the game object has the correct structure for ProjectDescription
    // Always extract text from translatable objects
    const normalizedGame = {
      ...game,
      title: extractedTitle
    };
    
    return normalizedGame;
  };

  render() {
    const { analytics, match } = this.props;
    const { loading, error } = analytics;
    const { gameId } = match.params;
    const { selectedProjectId } = this.state;
    
    const data = this.getAnalyticsData(); // Full array for multi-language components
    const aggregatedData = this.getAnalyticsDataAggregated(); // Aggregated data across all languages
    const dailyData = this.getDailyStatsData(); // Daily stats data
    const selectedProject = this.getSelectedProject();
    const normalizedGame = this.getNormalizedGame();

    return (
      <PageContainer id="analytics-dashboard">
        {gameId && (
          <GameDetails gameId={gameId}>
            <PageTitle>{this.getPageTitle()}</PageTitle>
          </GameDetails>
        )}
        
        {!gameId && (
          <>
            <PageTitle>{this.getPageTitle()}</PageTitle>
            <PageSubtitle>{this.getPageSubtitle()}</PageSubtitle>
          </>
        )}
        
        {/* Section 1: Filters and Fetch Button */}
        {this.renderControls()}

        {error && (
          <ErrorMessage>
            {error.includes('info@dized.com') ? error : `Error loading analytics: ${error}`}
          </ErrorMessage>
        )}

        {loading && (
          <LoadingMessage>
            Loading analytics data...
          </LoadingMessage>
        )}

        {!loading && !error && !data && selectedProjectId && (
          <NoDataMessage>
            No analytics data available for the selected project and time period.
          </NoDataMessage>
        )}

        {!error && data && selectedProject && normalizedGame && (
          <>
            {/* Section 1: Project Description */}
            <ProjectDescription 
              project={selectedProject} 
              game={this.getNormalizedGame()} 
            />

            {/* Section 2: Quick Stats - uses aggregated data across all languages */}
            <QuickStats data={aggregatedData} />

            {/* Section 3: Daily Activity Trends - uses daily stats data */}
            <DailyStats data={dailyData} />

            {/* Section 4: User Distribution - uses aggregated data across all languages */}
            <UserDistribution data={aggregatedData} />

            {/* Section 5: Language Distribution - uses full array to show language breakdown */}
            <LanguageDistribution data={data} />

            {/* Section 6: Progress Milestones - uses aggregated data across all languages */}
            <UserMilestones data={aggregatedData} />

            {/* Section 7: State Performance Analytics - uses aggregated data across all languages */}
            <StatePerformanceAnalytics data={aggregatedData} />

            {/* Section 8: Stats Overview - uses full array to show per-language breakdown */}
            <StatsOverview data={data} game={normalizedGame} />
          </>
        )}
      </PageContainer>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Analytics);
